/**
 * Everything the Quick Add screen needs, in one read and one write (Phase 1, step 1.2).
 *
 * Quick Add is feature number one and it is measured in seconds (spec v2 22.1), so the
 * options behind it are fetched as a single cached payload instead of four screens' worth of
 * hooks: accounts with their mirrored ledger accounts, expense categories in the order the
 * user will tap them, income sources, and the person's default account.
 *
 * The ledger account ids never reach the UI as a concept — they are what `app.post_entry`
 * speaks, resolved here once so no screen has to know the chart of accounts exists.
 */
import {
  buildDebtPaymentEntry,
  buildExpenseEntry,
  buildIncomeEntry,
  buildSplitEntry,
  buildTransferEntry,
  type AccountType,
  type PlanningNature,
  type PostEntryInput,
  type SplitAllocation,
  type TransferPurpose,
} from '@pfo/domain';
import type { Json } from '@pfo/database';

import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';
import { fetchPreferences } from '../../preferences/data/preferencesRepository.js';
import { usableDefaultAccountId } from '../model/missingFields.js';

export type QuickAddAccount = Readonly<{
  id: string;
  name: string;
  accountType: AccountType;
  /** The mirrored ledger account — what the RPC actually debits or credits. */
  ledgerAccountId: string;
}>;

export type QuickAddCategory = Readonly<{
  id: string;
  name: string;
  isFavorite: boolean;
  /** Icon key stored on the category, or null for the rows seeded at signup. */
  icon: string | null;
}>;

export type QuickAddIncomeSource = Readonly<{
  id: string;
  name: string;
  ledgerAccountId: string;
  defaultDestinationAccountId: string | null;
  isFavorite: boolean;
}>;

export type QuickAddOptions = Readonly<{
  /** The household's system "Expenses" ledger account. */
  expensesLedgerAccountId: string;
  accounts: readonly QuickAddAccount[];
  categories: readonly QuickAddCategory[];
  incomeSources: readonly QuickAddIncomeSource[];
  defaultAccountId: string | null;
}>;

export async function fetchQuickAddOptions(): Promise<QuickAddOptions> {
  const [ledgerResult, accountsResult, categoriesResult, sourcesResult, preferences] =
    await Promise.all([
      supabase
        .from('ledger_accounts')
        .select('id, name, is_system, linked_account_id, linked_income_source_id')
        .eq('is_active', true),
      supabase
        .from('accounts')
        .select('id, name, account_type')
        .eq('is_active', true)
        .order('sort_order')
        .order('name'),
      supabase
        .from('categories')
        .select('id, name, icon')
        .eq('category_type', 'expense')
        .eq('is_active', true)
        // Leaves only. The three parents are groupings for the report — nobody spends money
        // on "Chi tiêu sinh hoạt" (N10).
        .not('parent_id', 'is', null)
        .order('sort_order')
        .order('name'),
      supabase
        .from('income_sources')
        .select('id, name, default_destination_account_id')
        .eq('is_active', true)
        .order('name'),
      fetchPreferences(),
    ]);

  if (ledgerResult.error) throw appErrorException(ledgerResult.error);
  if (accountsResult.error) throw appErrorException(accountsResult.error);
  if (categoriesResult.error) throw appErrorException(categoriesResult.error);
  if (sourcesResult.error) throw appErrorException(sourcesResult.error);

  const ledgerRows = ledgerResult.data ?? [];
  const expensesLedgerAccountId = ledgerRows.find(
    (row) => row.is_system && row.name === 'Expenses',
  )?.id;
  if (expensesLedgerAccountId === undefined) {
    throw appErrorException(new Error('Không tìm thấy sổ chi phí của gia đình.'));
  }

  const ledgerIdByAccountId = new Map(
    ledgerRows
      .filter((row) => row.linked_account_id !== null)
      .map((row) => [row.linked_account_id!, row.id]),
  );
  const ledgerIdBySourceId = new Map(
    ledgerRows
      .filter((row) => row.linked_income_source_id !== null)
      .map((row) => [row.linked_income_source_id!, row.id]),
  );

  const favoriteCategories = new Set(preferences.favoriteCategoryIds);
  const favoriteSources = new Set(preferences.favoriteIncomeSourceIds);

  // An account with no mirrored ledger account cannot be posted to, so it is not offered.
  const accounts: QuickAddAccount[] = (accountsResult.data ?? []).flatMap((row) => {
    const ledgerAccountId = ledgerIdByAccountId.get(row.id);
    return ledgerAccountId === undefined
      ? []
      : [
          {
            id: row.id,
            name: row.name,
            accountType: row.account_type as AccountType,
            ledgerAccountId,
          },
        ];
  });

  return {
    expensesLedgerAccountId,
    accounts,
    // Favourites first, then the household's own order: the fewer taps to the category the
    // family actually uses, the more likely the app gets used at all.
    categories: (categoriesResult.data ?? [])
      .map((row) => ({
        id: row.id,
        name: row.name,
        isFavorite: favoriteCategories.has(row.id),
        icon: row.icon,
      }))
      .sort((left, right) => Number(right.isFavorite) - Number(left.isFavorite)),
    // Favourites first here too — an option list cannot show a star without putting it in the
    // label, so the order is the whole signal.
    incomeSources: (sourcesResult.data ?? [])
      .flatMap((row) => {
        const ledgerAccountId = ledgerIdBySourceId.get(row.id);
        return ledgerAccountId === undefined
          ? []
          : [
              {
                id: row.id,
                name: row.name,
                ledgerAccountId,
                defaultDestinationAccountId: row.default_destination_account_id,
                isFavorite: favoriteSources.has(row.id),
              },
            ];
      })
      .sort((left, right) => Number(right.isFavorite) - Number(left.isFavorite)),
    // AJ2: an archived default would pre-select nothing and hide why — offer none instead.
    defaultAccountId: usableDefaultAccountId(preferences.defaultAccountId, accounts),
  };
}

export type SubjectContext = Readonly<{
  selfSubjectId: string;
  householdSubjectId: string;
}>;

export type ExpenseDraft = Readonly<{
  amount: number;
  categoryId: string;
  accountId: string;
  occurredAt: string;
  note?: string;
  planningNature?: PlanningNature;
  forFamily?: boolean;
}>;

export type IncomeDraft = Readonly<{
  amount: number;
  incomeSourceId: string;
  destinationAccountId: string;
  occurredAt: string;
  note?: string;
}>;

export type TransferDraft = Readonly<{
  amount: number;
  fromAccountId: string;
  toAccountId: string;
  occurredAt: string;
  note?: string;
  purpose?: TransferPurpose;
}>;

/** Paying down a loan or settling a card statement — the destination is a liability. */
export type DebtPaymentDraft = Readonly<{
  amount: number;
  fromAccountId: string;
  liabilityAccountId: string;
  occurredAt: string;
  note?: string;
}>;

/** One destination of a split, in the screen's own words (account ids, not ledger ids). */
export type SplitAllocationDraft =
  | Readonly<{ kind: 'transfer'; toAccountId: string; amount: number; purpose?: TransferPurpose }>
  | Readonly<{
      kind: 'expense';
      categoryId: string;
      amount: number;
      planningNature?: PlanningNature;
    }>;

export type SplitDraft = Readonly<{
  sourceAccountId: string;
  occurredAt: string;
  note?: string;
  allocations: readonly SplitAllocationDraft[];
}>;

/**
 * Posts the expense and waits for the database to confirm it (skill 11.1 — a financial write
 * is never shown as done before it is persisted).
 */
export function postExpense(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: ExpenseDraft,
): Promise<string> {
  return postEntry(buildExpensePayload(options, subjects, draft));
}

export function postIncome(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: IncomeDraft,
): Promise<string> {
  return postEntry(buildIncomePayload(options, subjects, draft));
}

export function postTransfer(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: TransferDraft,
): Promise<string> {
  return postEntry(buildTransferPayload(options, subjects, draft));
}

export function postDebtPayment(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: DebtPaymentDraft,
): Promise<string> {
  return postEntry(buildDebtPaymentPayload(options, subjects, draft));
}

export function postSplit(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: SplitDraft,
): Promise<string> {
  return postEntry(buildSplitPayload(options, subjects, draft));
}

/** Replaces an existing entry in place, same payload shape as posting it (A7). */
export async function updateEntry(entryId: string, payload: PostEntryInput): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('update_entry', { p_entry_id: entryId, p: payload as unknown as Json });
  if (error) throw appErrorException(error);
}

export function buildExpensePayload(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: ExpenseDraft,
): PostEntryInput {
  return buildExpenseEntry({
    selfSubjectId: subjects.selfSubjectId,
    householdSubjectId: subjects.householdSubjectId,
    accountLedgerAccountId: ledgerAccountIdOf(options, draft.accountId),
    ledgerExpenseAccountId: options.expensesLedgerAccountId,
    amount: draft.amount,
    categoryId: draft.categoryId,
    categoryType: 'expense',
    occurredAt: draft.occurredAt,
    ...(draft.note === undefined ? {} : { note: draft.note }),
    ...(draft.planningNature === undefined ? {} : { planningNature: draft.planningNature }),
    ...(draft.forFamily === undefined ? {} : { forFamily: draft.forFamily }),
  });
}

export function buildIncomePayload(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: IncomeDraft,
): PostEntryInput {
  const source = options.incomeSources.find((item) => item.id === draft.incomeSourceId);
  if (source === undefined) {
    throw appErrorException(new Error('Nguồn thu này không còn nữa.'));
  }

  return buildIncomeEntry({
    subjectId: subjects.selfSubjectId,
    incomeSourceLedgerAccountId: source.ledgerAccountId,
    destinationLedgerAccountId: ledgerAccountIdOf(options, draft.destinationAccountId),
    amount: draft.amount,
    occurredAt: draft.occurredAt,
    ...(draft.note === undefined ? {} : { note: draft.note }),
  });
}

export function buildTransferPayload(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: TransferDraft,
): PostEntryInput {
  return buildTransferEntry({
    subjectId: subjects.selfSubjectId,
    fromLedgerAccountId: ledgerAccountIdOf(options, draft.fromAccountId),
    toLedgerAccountId: ledgerAccountIdOf(options, draft.toAccountId),
    amount: draft.amount,
    occurredAt: draft.occurredAt,
    ...(draft.note === undefined ? {} : { note: draft.note }),
  });
}

export function buildDebtPaymentPayload(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: DebtPaymentDraft,
): PostEntryInput {
  return buildDebtPaymentEntry({
    subjectId: subjects.selfSubjectId,
    liabilityLedgerAccountId: ledgerAccountIdOf(options, draft.liabilityAccountId),
    fromLedgerAccountId: ledgerAccountIdOf(options, draft.fromAccountId),
    amount: draft.amount,
    occurredAt: draft.occurredAt,
    ...(draft.note === undefined ? {} : { note: draft.note }),
  });
}

export function buildSplitPayload(
  options: QuickAddOptions,
  subjects: SubjectContext,
  draft: SplitDraft,
): PostEntryInput {
  return buildSplitEntry({
    subjectId: subjects.selfSubjectId,
    sourceLedgerAccountId: ledgerAccountIdOf(options, draft.sourceAccountId),
    ledgerExpenseAccountId: options.expensesLedgerAccountId,
    occurredAt: draft.occurredAt,
    ...(draft.note === undefined ? {} : { note: draft.note }),
    allocations: draft.allocations.map((allocation): SplitAllocation =>
      allocation.kind === 'transfer'
        ? {
            kind: 'transfer',
            toLedgerAccountId: ledgerAccountIdOf(options, allocation.toAccountId),
            amount: allocation.amount,
            ...(allocation.purpose === undefined ? {} : { purpose: allocation.purpose }),
          }
        : {
            kind: 'expense',
            categoryId: allocation.categoryId,
            amount: allocation.amount,
            ...(allocation.planningNature === undefined
              ? {}
              : { planningNature: allocation.planningNature }),
          },
    ),
  });
}

function ledgerAccountIdOf(options: QuickAddOptions, accountId: string): string {
  const account = options.accounts.find((item) => item.id === accountId);
  if (account === undefined) {
    throw appErrorException(new Error('Tài khoản này không còn dùng được.'));
  }
  return account.ledgerAccountId;
}

async function postEntry(payload: PostEntryInput): Promise<string> {
  const { data, error } = await supabase
    .schema('app')
    .rpc('post_entry', { p: payload as unknown as Json });
  if (error) throw appErrorException(error);
  return data;
}
